import { rfidTagApi } from "#/api/rfidTagApi";
import { showSuccessNotification } from "#/components/notifications";
import type { RfidTag, RfidTagType } from "#/types/rfidTag";
import { Button, Divider, Modal, Radio, Text, Title } from "@mantine/core";
import { IconBox, IconCar } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

interface EditRfidTagModalProps {
  opened: boolean;
  onClose: () => void;
  rfidTag: RfidTag | undefined;
  onSuccess: () => void;
}

const EditRfidTagModal = ({
  opened,
  onClose,
  rfidTag,
  onSuccess,
}: EditRfidTagModalProps) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const [selectedTagType, setSelectedTagType] = useState<"hard" | "label">("hard");

  useEffect(() => {
    if (rfidTag?.type) {
      setSelectedTagType(rfidTag.type as "hard" | "label");
    }
  }, [rfidTag]);

  const linkRfidTagMutation = useMutation({
    mutationFn: async ({
      id,
      type,
    }: {
      id: number;
      type: RfidTagType;
    }) => {
      return await rfidTagApi.update(id, {
        type: type,
      });
    },
    onSuccess: () => {
      showSuccessNotification({
        message: t("rfidTags.editTag.success"),
      });
      onClose();
      onSuccess();
    },
    onError: (error) => {
      console.log("🚀 ~ linkRfidTag ~ error:", error);
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={t("rfidTags.editTag.title")}
      closeOnClickOutside={false}
      size="lg"
      classNames={{
        title: "font-bold",
      }}
    >
      <div className="w-full flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <span className="text-sm">
            {t("rfidTags.editTag.description", { rfid: "" })}{" "}
            <strong className="font-mono text-black">{rfidTag?.rfid}</strong>
          </span>
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <Title order={6}>{t("rfidTags.editTag.tagType")}</Title>
            <Text size="sm" c="gray.6" mb="sm">
              {t("rfidTags.editTag.tagTypeDescription")}
            </Text>
            <Radio.Group
              value={selectedTagType}
              onChange={(value) =>
                setSelectedTagType(value as "hard" | "label")
              }
              withAsterisk
            >
              <div className="flex gap-4">
                <Radio value="hard" label={t("rfidTags.editTag.hardTag")} disabled={!canWrite()} />
                <Radio value="label" label={t("rfidTags.editTag.labelTag")} disabled={!canWrite()} />
              </div>
            </Radio.Group>
          </div>

          {(rfidTag?.item || rfidTag?.vehicle) && (
            <>
              <Divider />

              <div>
                <Title order={6}>{t("rfidTags.editTag.currentlyLinkedTo")}</Title>
                <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                  {rfidTag?.item && (
                    <div className="flex items-center gap-2">
                      <IconBox size={16} className="text-green-600" />
                      <div>
                        <Text size="sm" fw={500}>
                          {rfidTag.item.product?.name ||
                            `Item #${rfidTag.item.id}`}
                        </Text>
                        <Text size="xs" c="gray.6">
                          {t("rfidTags.editTag.itemNumber", { id: rfidTag.item.id })}
                        </Text>
                      </div>
                    </div>
                  )}
                  {rfidTag?.vehicle && (
                    <div className="flex items-center gap-2">
                      <IconCar size={16} className="text-violet-600" />
                      <div>
                        <Text size="sm" fw={500}>
                          {rfidTag.vehicle.name}
                        </Text>
                        <Text size="xs" c="gray.6">
                          {t("rfidTags.editTag.vehicleNumber", { id: rfidTag.vehicle.id })}
                        </Text>
                      </div>
                    </div>
                  )}
                </div>
                <Text size="xs" c="gray.6" mt="xs">
                  {t("rfidTags.editTag.linkUnlinkHint")}
                </Text>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end">
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              onClick={() => {
                if (rfidTag) {
                  linkRfidTagMutation.mutate({
                    id: rfidTag.id,
                    type: selectedTagType as RfidTagType,
                  });
                }
              }}
              loading={linkRfidTagMutation.isPending}
              disabled={!canWrite()}
            >
              {t("rfidTags.editTag.updateTagType")}
            </Button>
          </PermissionTooltip>
        </div>
      </div>
    </Modal>
  );
};

export default EditRfidTagModal;
